<template>
    <span>
        <span @click="start">
            <slot :processing="processing" />
        </span>

        <Dialog :open="confirming" @update:open="closeModal">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{{ title }}</DialogTitle>
                </DialogHeader>

                <p class="text-sm text-muted-foreground">
                    {{ content }}
                </p>

                <div class="flex justify-center py-4">
                    <OtpInput
                        input-classes="mt-2 rounded-md text-center border border-new-neutral-300 mx-1 sm:mx-2 focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-hidden transition duration-200 ease-in-out"
                        separator=""
                        :num-inputs="6"
                        :should-auto-focus="true"
                        :is-input-num="true"
                        :error="error"
                        @on-complete="onComplete"
                    />
                </div>

                <DialogFooter>
                    <Button size="lg" variant="ghost" @click="closeModal">Cancel</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </span>
</template>

<script lang="ts" setup>
import { ref, nextTick } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import OtpInput from '@/Pages/Auth/components/OtpInput.vue';

withDefaults(defineProps<{
    title?: string;
    content?: string;
}>(), {
    title: 'Verify your identity',
    content: 'We sent a 6-digit code to your email address. Please enter it below to continue.',
});

const emit = defineEmits<{
    confirmed: [];
}>();

const confirming = ref(false);
const processing = ref(false);
const error = ref('');

const start = () => {
    if (processing.value || confirming.value) return;

    processing.value = true;
    error.value = '';

    axios
        .post(route('user.otp.store'))
        .then(() => {
            processing.value = false;
            confirming.value = true;
        })
        .catch((err) => {
            processing.value = false;
            toast.error(err?.response?.data?.message ?? 'Failed to send verification code. Please try again.');
        });
};

const onComplete = (code: string) => {
    processing.value = true;
    error.value = '';

    axios
        .put(route('user.otp.update'), { code })
        .then(() => {
            processing.value = false;
            closeModal();
            nextTick(() => emit('confirmed'));
        })
        .catch((err) => {
            processing.value = false;
            error.value = err?.response?.data?.errors?.code?.[0] ?? 'Invalid code. Please try again.';
        });
};

const closeModal = () => {
    confirming.value = false;
    error.value = '';
};
</script>
